import { ApiSchema } from "@api-types";

export namespace FiltersHelper {
  export const getResponsesFilters = (
    filterIds: {
      [x: string]: number[];
    },
    questionOptionIds: {
      [x: number]: {
        [x: number]: boolean;
      };
    },
    panelType: ApiSchema.PanelType | undefined,
    regionId?: number | null,
    // Making these optional for now for the Groups.bloc (but should probably be added)
    selectedSurveyRunIds?: number[] | null, // | undefined,
    selectedDates?: [Date | null, Date | null] | null, // | undefined,
    categorySegmentIds?: number[],
  ) => {
    const flatFilters = Object.values(filterIds ?? {}).flat();
    const includeIds = filterSubDictionaryByValue(questionOptionIds, true)
      .map((a) => a.ids)
      .flat();
    const excludeIds = filterSubDictionaryByValue(questionOptionIds, false)
      .map((a) => a.ids)
      .flat();
    return {
      filterIds: flatFilters,
      excludeQuestionOptionsIds: excludeIds,
      regionIds: [regionId],
      projectGroupSegmentIds: categorySegmentIds ?? undefined,
      questionOptionsIds: includeIds,
      testOptionIds: undefined,
      surveyIds: selectedSurveyRunIds ?? undefined,
      panelType,
      dateFrom: selectedDates?.[0] ? selectedDates[0].toISOString() : undefined,
      dateTo: selectedDates?.[1] ? selectedDates[1].toISOString() : undefined,
    } as ApiSchema.SegmentResponsesFilters;
  };
  export const getGroupSegmentFilters = (
    filterIds: {
      [x: string]: number[];
    },
    questionOptionIds: {
      [x: number]: {
        [x: number]: boolean;
      };
    },
    uandAOptionIds: {
      [x: number]: {
        [x: number]: boolean;
      };
    },
    panelType: ApiSchema.PanelType | undefined,
    selectedDates?: [Date | null, Date | null], // | undefined,
    categorySegmentIds?: number[],
  ) => {
    const flatFilters = Object.values(filterIds ?? {}).flat();
    const includeIds = filterSubDictionaryByValue(questionOptionIds, true)
      .map((a) => a.ids)
      .flat();
    const excludeIds = filterSubDictionaryByValue(questionOptionIds, false)
      .map((a) => a.ids)
      .flat();
    const uandAIncludeIds = filterSubDictionaryByValue(uandAOptionIds, true)
      .map((a) => a.ids)
      .flat();
    const uandAExcludeIds = filterSubDictionaryByValue(uandAOptionIds, false)
      .map((a) => a.ids)
      .flat();
    return {
      filterIds: flatFilters,
      excludeQuestionOptionsIds: excludeIds,
      questionOptionsIds: includeIds,
      uandAQuestionOptionsIds: uandAIncludeIds,
      excludeUandAQuestionOptionsIds: uandAExcludeIds,
      testOptionIds: undefined,
      standardQuestionIds: undefined,
      panelType,
      projectGroupSegmentIds: categorySegmentIds ?? undefined,
      dateFrom: selectedDates?.[0] ? selectedDates[0].toISOString() : undefined,
      dateTo: selectedDates?.[1] ? selectedDates[1].toISOString() : undefined,
    } as ApiSchema.GroupSegmentFilter;
  };

  export const compareFilters = (
    filter1: ApiSchema.SegmentResponsesFilters | ApiSchema.GroupResponsesFilters,
    filter2: ApiSchema.SegmentResponsesFilters | ApiSchema.GroupResponsesFilters,
  ) => {
    if (
      // check basic types
      (filter1.dateTo ?? null) !== (filter2.dateTo ?? null) ||
      (filter1.dateFrom ?? null) !== (filter2.dateFrom ?? null) ||
      (filter1.panelType ?? null) !== (filter2.panelType ?? null) ||
      // check length
      (filter1.regionIds ?? []).length !== (filter2.regionIds ?? []).length ||
      (filter1.filterIds ?? []).length !== (filter2.filterIds ?? []).length ||
      (filter1.questionOptionsIds ?? []).length !== (filter2.questionOptionsIds ?? []).length ||
      (filter1.standardQuestionIds ?? []).length !== (filter2.standardQuestionIds ?? []).length ||
      (filter1.excludeQuestionOptionsIds ?? []).length !== (filter2.excludeQuestionOptionsIds ?? []).length ||
      (filter1.surveyIds ?? []).length !== (filter2.surveyIds ?? []).length ||
      // check values
      (filter1.regionIds?.length &&
        !!filter1.regionIds?.filter((f1) => !filter2.regionIds?.find((f2) => f2 === f1)).length) ||
      (filter1.filterIds?.length &&
        !!filter1.filterIds?.filter((f1) => !filter2.filterIds?.find((f2) => f2 === f1)).length) ||
      (filter1.questionOptionsIds?.length &&
        !!filter1.questionOptionsIds?.filter((f1) => !filter2.questionOptionsIds?.find((f2) => f2 === f1))
          .length) ||
      (filter1.standardQuestionIds?.length &&
        !!filter1.standardQuestionIds?.filter((f1) => !filter2.standardQuestionIds?.find((f2) => f2 === f1))
          .length) ||
      (filter1.excludeQuestionOptionsIds?.length &&
        !!filter1.excludeQuestionOptionsIds?.filter(
          (f1) => !filter2.excludeQuestionOptionsIds?.find((f2) => f2 === f1),
        ).length) ||
      (filter1.surveyIds?.length &&
        !!filter1.surveyIds?.filter((f1) => !filter2.surveyIds?.find((f2) => f2 === f1)).length)
    ) {
      return false;
    }
    return true;
  };
  export const filterDictionaryByValue = (dictionary: { [key in number]: boolean }, value: boolean) =>
    Object.keys(dictionary)
      .map((a) => Number(a))
      .filter((a) => dictionary[a] === value);

  export const filterSubDictionaryByValue = (
    dictionary: { [key in number]: { [key in number]: boolean } },
    value: boolean,
  ) =>
    Object.keys(dictionary)
      .map((a) => Number(a))
      ?.map((questionId) => ({
        questionId,
        ids: Object.keys(dictionary[questionId] ?? {})
          .map((a) => Number(a))
          .filter((a) => dictionary[questionId]![a] === value),
      }));
  export const getDictionaryGroupedByValue = (dictionary: {
    [key in number]: { [key in number]: boolean };
  }) =>
    Object.keys(dictionary)
      .map((a) => Number(a))
      ?.map((questionId) => ({
        questionId,
        trueIds: Object.keys(dictionary[questionId] ?? {})
          .map((a) => Number(a))
          .filter((a) => dictionary[questionId]![a] === true),
        falseIds: Object.keys(dictionary[questionId] ?? {})
          .map((a) => Number(a))
          .filter((a) => dictionary[questionId]![a] === false),
      }));

  /**
   * Reverses all boolean values in a question options dictionary.
   * @param questionOptions - Dictionary of question IDs to option IDs to boolean values
   * @returns New dictionary with all boolean values flipped (true -> false, false -> true)
   */
  export const reverseQuestionOptions = (questionOptions: {
    [x: number]: {
      [x: number]: boolean;
    };
  }): {
    [x: number]: {
      [x: number]: boolean;
    };
  } => {
    const reversed: {
      [x: number]: {
        [x: number]: boolean;
      };
    } = {};
    for (const [questionId, options] of Object.entries(questionOptions)) {
      reversed[Number(questionId)] = {};
      for (const [optionId, value] of Object.entries(options)) {
        reversed[Number(questionId)]![Number(optionId)] = !value;
      }
    }
    return reversed;
  };

  /**
   * Reverses filter selections by returning all available options NOT currently selected.
   * Used for "remaining" audience filters where we want to invert the selection.
   * Only reverses categories that actually have selections - empty categories are not reversed.
   * @param selectedFilters - Dictionary of filter category IDs (as strings) to arrays of selected option IDs
   * @param availableFilterOptions - Map of filter category IDs to all available option IDs for that category
   * @returns New dictionary with inverted selections (all options NOT in the original selection)
   */
  export const reverseFilters = (
    selectedFilters: {
      [x: string]: number[];
    },
    availableFilterOptions: Map<string, number[]>,
  ): {
    [x: string]: number[];
  } => {
    const reversed: {
      [x: string]: number[];
    } = {};

    // Only reverse categories that actually have selections
    for (const [categoryId, selectedOptions] of Object.entries(selectedFilters)) {
      // Skip empty selections - they shouldn't be reversed
      if (!selectedOptions || selectedOptions.length === 0) {
        continue;
      }

      const allOptions = availableFilterOptions.get(categoryId);
      if (!allOptions) {
        continue;
      }

      // Get all options that are NOT currently selected
      const invertedOptions = allOptions.filter((optionId) => !selectedOptions.includes(optionId));
      // Only include if there are inverted options
      if (invertedOptions.length > 0) {
        reversed[categoryId] = invertedOptions;
      }
    }

    return reversed;
  };

  /**
   * Expands a segment's responsesFilters into the FE filter URL param shape, so
   * the destination page can render the segment
   */
  export const convertSegmentToUrlParams = (
    segment: { responsesFilters?: ApiSchema.SegmentResponsesFilters | null } | undefined,
    systemQuestionOptions: ApiSchema.ProjectSystemQuestionOption[],
    allQuestions: {
      questionId: number;
      question: { questionOptions: Array<{ id: number }> | null };
    }[],
  ): Record<string, string | number | (string | number)[] | undefined | null> => {
    const r = segment?.responsesFilters;
    if (!r) return {};
    const out: Record<string, string | number | (string | number)[] | undefined | null> = {};

    if (r.filterIds?.length) {
      const byCode = systemQuestionOptions
        .filter((o) => r.filterIds?.includes(o.id))
        .reduce<Record<string, number[]>>((acc, o) => {
          (acc[o.questionCode] ??= []).push(o.id);
          return acc;
        }, {});
      if (Object.keys(byCode).length) out.selectedFilterIds = JSON.stringify(byCode);
    }

    const optionIds = [...(r.questionOptionsIds ?? []), ...(r.excludeQuestionOptionsIds ?? [])];
    if (optionIds.length) {
      const byQuestion = allQuestions.reduce<Record<number, Record<number, boolean>>>((acc, q) => {
        const matches = q.question.questionOptions?.filter((o) => optionIds.includes(o.id)) ?? [];
        if (matches.length) {
          acc[q.questionId] = matches.reduce<Record<number, boolean>>((p, o) => {
            p[o.id] = !!r.questionOptionsIds?.includes(o.id);
            return p;
          }, {});
        }
        return acc;
      }, {});
      if (Object.keys(byQuestion).length) out.selectedQuestionOptionFilterIds = JSON.stringify(byQuestion);
    }

    if (r.surveyIds?.length) out.selectedSurveyRunIds = r.surveyIds;
    if (r.testOptionIds?.length) out.selectedTestOptionIds = r.testOptionIds;
    if (r.panelType) out.selectedPanelType = r.panelType;
    if (r.regionIds?.length) out.selectedRegionId = r.regionIds[0];
    if (r.projectGroupSegmentIds?.length) out.selectedProjectGroupSegmentIds = r.projectGroupSegmentIds;

    return out;
  };

  /**
   * Converts a fact's scope columns (region, segment, demographic, screener, test option)
   * to URL parameters for navigating to the corresponding filtered data view.
   *
   * If `segments` is provided and the fact has a segmentId, the segment's filters are
   * expanded into URL params alongside the segmentId so the destination doesn't appear
   * "edited" relative to the saved segment.
   */
  export const convertFactScopeToUrlParams = ({
    regionId,
    segmentId,
    testOptionId,
    demographicOptionId,
    screenerOptionId,
    projectGroupSegmentId,
    allQuestions,
    systemQuestionOptions,
    segments,
  }: {
    regionId: number;
    segmentId?: number | null;
    testOptionId?: number | null;
    demographicOptionId?: number | null;
    screenerOptionId?: number | null;
    projectGroupSegmentId?: number | null;
    allQuestions: {
      questionId: number;
      question: { questionOptions: Array<{ id: number }> | null };
    }[];
    systemQuestionOptions: ApiSchema.ProjectSystemQuestionOption[];
    segments?: Array<{ id: number; responsesFilters?: ApiSchema.SegmentResponsesFilters | null }>;
  }): Record<string, string | number | (string | number)[] | undefined | null> => {
    const segmentParams = segmentId
      ? convertSegmentToUrlParams(
          segments?.find((s) => s.id === segmentId),
          systemQuestionOptions,
          allQuestions,
        )
      : {};

    const urlParams: Record<string, string | number | (string | number)[] | undefined | null> = {
      ...segmentParams,
      selectedRegionId: regionId,
    };

    if (testOptionId) {
      urlParams.selectedTestOptionIds = [testOptionId];
    }

    if (segmentId) {
      urlParams.segmentId = segmentId;
    }

    if (demographicOptionId) {
      const systemOption = systemQuestionOptions.find((opt) => opt.id === demographicOptionId);
      if (systemOption) {
        urlParams.selectedFilterIds = JSON.stringify({
          [systemOption.questionCode]: [demographicOptionId],
        });
      }
    }

    if (screenerOptionId) {
      for (const projectQuestion of allQuestions) {
        const option = projectQuestion.question.questionOptions?.find((opt) => opt.id === screenerOptionId);
        if (option) {
          urlParams.selectedQuestionOptionFilterIds = JSON.stringify({
            [projectQuestion.questionId]: { [screenerOptionId]: true },
          });
          break;
        }
      }
    }

    if (projectGroupSegmentId) {
      urlParams.selectedProjectGroupSegmentIds = [projectGroupSegmentId];
    }

    return urlParams;
  };
}
